import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { priceService } from '../../services/priceService';
import { carrierService } from '../../services/carrierService';
import { regionService } from '../../services/regionService';
import { vehicleService } from '../../services/vehicleService';

const PriceSchema = Yup.object().shape({
  carrierId: Yup.number().required('Taşıyıcı seçimi zorunludur'),
  fromRegionId: Yup.number().required('Başlangıç bölgesi seçimi zorunludur'),
  toRegionId: Yup.number().required('Varış bölgesi seçimi zorunludur'),
  vehicleTypeId: Yup.number().required('Araç tipi seçimi zorunludur'),
  price: Yup.number().required('Fiyat zorunludur').positive('Fiyat pozitif olmalıdır'),
  currency: Yup.string().required('Para birimi zorunludur'),
  validFrom: Yup.date().nullable(),
  validTo: Yup.date().nullable().min(
    Yup.ref('validFrom'),
    'Geçerlilik bitiş tarihi, başlangıç tarihinden sonra olmalıdır'
  )
});

const PriceEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [price, setPrice] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchPrice();
    fetchCarriers();
    fetchRegions();
    fetchVehicleTypes();
  }, [id]);

  const fetchPrice = async () => {
    try {
      const response = await priceService.getPriceById(id);
      if (response && response.success && response.data) {
        setPrice(response.data);
      } else {
        throw new Error('Fiyat bilgileri alınamadı');
      }
    } catch (error) {
      toast.error('Fiyat bilgileri yüklenirken bir hata oluştu');
      navigate('/prices');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCarriers = async () => {
    try {
      const response = await carrierService.getCarriers({ isActive: true });
      if (response.data) {
        setCarriers(response.data.rows);
      }
    } catch (error) {
      toast.error('Taşıyıcılar yüklenirken bir hata oluştu');
      console.error('Error fetching carriers:', error);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await regionService.getRegions();
      if (response.data) {
        setRegions(response.data.regions);
      }
    } catch (error) {
      toast.error('Bölgeler yüklenirken bir hata oluştu');
      console.error('Error fetching regions:', error);
    }
  };

  const fetchVehicleTypes = async () => {
    try {
      const response = await vehicleService.getVehicleTypes();
      if (response.data) {
        setVehicleTypes(response.data.vehicleTypes);
      }
    } catch (error) {
      toast.error('Araç tipleri yüklenirken bir hata oluştu');
      console.error('Error fetching vehicle types:', error);
    }
  };

  const handleSubmit = async (values) => {
    setIsSaving(true);
    try {
      await priceService.updatePrice(id, values);
      toast.success('Fiyat başarıyla güncellendi');
      navigate('/prices');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Fiyat güncellenirken bir hata oluştu');
      console.error('Error updating price:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!price) {
    return (
      <div className="text-center p-12">
        <p className="text-gray-500">Fiyat bulunamadı.</p>
      </div>
    );
  }

  // Tarih formatını düzelt (YYYY-MM-DD)
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const initialValues = {
    carrierId: price.carrier_id || '',
    fromRegionId: price.from_region_id || '',
    toRegionId: price.to_region_id || '',
    vehicleTypeId: price.vehicle_type_id || '',
    price: price.price || '',
    currency: price.currency || 'EUR',
    validFrom: formatDate(price.valid_from),
    validTo: formatDate(price.valid_to)
  };

  return (
    <div>
      <div className="pb-5 border-b border-gray-200">
        <h1 className="text-2xl font-semibold text-gray-900">Fiyat Düzenle</h1>
      </div>

      <div className="mt-6 bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
        <Formik
          initialValues={initialValues}
          validationSchema={PriceSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ errors, touched, setFieldValue, values }) => (
            <Form className="space-y-6">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <label htmlFor="carrierId" className="block text-sm font-medium text-gray-700">
                    Taşıyıcı *
                  </label>
                  <div className="mt-1">
                    <Field
                      as="select"
                      name="carrierId"
                      id="carrierId"
                      className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                        errors.carrierId && touched.carrierId ? 'border-red-500' : ''
                      }`}
                    >
                      <option value="">Seçiniz</option>
                      {carriers.map(carrier => (
                        <option key={carrier.id} value={carrier.id}>{carrier.name}</option>
                      ))}
                    </Field>
                    <ErrorMessage name="carrierId" component="div" className="mt-1 text-sm text-red-600" />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="vehicleTypeId" className="block text-sm font-medium text-gray-700">
                    Araç Tipi *
                  </label>
                  <div className="mt-1">
                    <Field
                      as="select"
                      name="vehicleTypeId"
                      id="vehicleTypeId"
                      className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                        errors.vehicleTypeId && touched.vehicleTypeId ? 'border-red-500' : ''
                      }`}
                    >
                      <option value="">Seçiniz</option>
                      {vehicleTypes.map(type => (
                        <option key={type.id} value={type.id}>{type.name} (Kapasite: {type.capacity})</option>
                      ))}
                    </Field>
                    <ErrorMessage name="vehicleTypeId" component="div" className="mt-1 text-sm text-red-600" />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="fromRegionId" className="block text-sm font-medium text-gray-700">
                    Başlangıç Bölgesi *
                  </label>
                  <div className="mt-1">
                    <Field
                      as="select"
                      name="fromRegionId"
                      id="fromRegionId"
                      className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                        errors.fromRegionId && touched.fromRegionId ? 'border-red-500' : ''
                      }`}
                    >
                      <option value="">Seçiniz</option>
                      {regions.map(region => (
                        <option key={region.id} value={region.id}>{region.name}</option>
                      ))}
                    </Field>
                    <ErrorMessage name="fromRegionId" component="div" className="mt-1 text-sm text-red-600" />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="toRegionId" className="block text-sm font-medium text-gray-700">
                    Varış Bölgesi *
                  </label>
                  <div className="mt-1">
                    <Field
                      as="select"
                      name="toRegionId"
                      id="toRegionId"
                      className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                        errors.toRegionId && touched.toRegionId ? 'border-red-500' : ''
                      }`}
                    >
                      <option value="">Seçiniz</option>
                      {regions.map(region => (
                        <option key={region.id} value={region.id}>{region.name}</option>
                      ))}
                    </Field>
                    <ErrorMessage name="toRegionId" component="div" className="mt-1 text-sm text-red-600" />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                    Fiyat *
                  </label>
                  <div className="mt-1">
                    <Field
                      type="number"
                      name="price"
                      id="price"
                      step="0.01"
                      className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                        errors.price && touched.price ? 'border-red-500' : ''
                      }`}
                    />
                    <ErrorMessage name="price" component="div" className="mt-1 text-sm text-red-600" />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="currency" className="block text-sm font-medium text-gray-700">
                    Para Birimi *
                  </label>
                  <div className="mt-1">
                    <Field
                      as="select"
                      name="currency"
                      id="currency"
                      className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                        errors.currency && touched.currency ? 'border-red-500' : ''
                      }`}
                    >
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                      <option value="TRY">TRY</option>
                      <option value="GBP">GBP</option>
                    </Field>
                    <ErrorMessage name="currency" component="div" className="mt-1 text-sm text-red-600" />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="validFrom" className="block text-sm font-medium text-gray-700">
                    Geçerlilik Başlangıcı
                  </label>
                  <div className="mt-1">
                    <Field
                      type="date"
                      name="validFrom"
                      id="validFrom"
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                    <ErrorMessage name="validFrom" component="div" className="mt-1 text-sm text-red-600" />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="validTo" className="block text-sm font-medium text-gray-700">
                    Geçerlilik Bitişi
                  </label>
                  <div className="mt-1">
                    <Field
                      type="date"
                      name="validTo"
                      id="validTo"
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                    <ErrorMessage name="validTo" component="div" className="mt-1 text-sm text-red-600" />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/prices')}
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                    isSaving ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isSaving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default PriceEdit;
